import { zodResolver } from '@hookform/resolvers/zod';
import { Autocomplete, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { VyIcon } from '@vy/ui-design-system';
import { PERMISSIONS, ROLES, type Role } from '@vy/types';
import { validateNotNullOrUndefined } from '@vy/utils';
import { capitalize } from 'lodash';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useInviteUser } from '../../../../../../../hooks/requests/use-invite-user';
import { queryKeys } from '../../../../../../../services/query-key/query-keys';
import { renderWithPermissions } from '../../../../../../../services/render-with-permissions';
import { getLocalStorageString } from '../../../../../../../utils/local-storage';
import { runMutation } from '../../../../../../../utils/vy-mutation';
import { InvitationsTable } from '../InvitationsTable/InvitationsTable';
import { RoleLabelValuePair } from '../MembersTable/members-table-types/role-label-value-pair';
import {
  InviteMemberFormSchema,
  type InviteMemberFormValues,
} from './invite-new-member-types/invite-member-form-schema';

const roleOptions = Object.values(ROLES).map(mapRoleToLabelValuePair);

export const InviteNewMember: React.FC = () => {
  const inviteUserMutation = useInviteUser();
  const queryClient = useQueryClient();

  const currentUserId = getLocalStorageString('userId');
  validateNotNullOrUndefined(currentUserId);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { isValid },
  } = useForm<InviteMemberFormValues>({
    resolver: zodResolver(InviteMemberFormSchema),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      role: ROLES.Viewer,
    },
    mode: 'onChange',
  });

  const onSubmit = handleSubmit(async (data) => {
    await runMutation(
      inviteUserMutation,
      {
        username: data.email,
        role: data.role,
        invitedBy: currentUserId,
        firstName: data.firstName,
        lastName: data.lastName,
      },
      {
        pending: 'Inviting member...',
        success: 'Member invited successfully',
        error: 'Failed to invite member',
      },
    );
    reset();

    await Promise.all([
      queryClient.invalidateQueries(queryKeys.users()),
      queryClient.invalidateQueries(queryKeys.invitations()),
    ]);
  });

  return (
    <Stack spacing={4} sx={{
      paddingX: "20px"
    }}>
      <Stack
        direction="row"
        sx={{
          gap: 4,
          justifyContent: "space-between",
          flexWrap: "wrap"
        }}>
        <Box sx={{
          flex: 1
        }}>
          <Typography>Invite a new member</Typography>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Invite new members by email address
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flex: 2 }}>
          {renderWithPermissions([PERMISSIONS.USER_INVITE], (hasPermission) => (
            <Stack spacing={2} sx={{ width: '100%' }}>
              <Stack direction="row" spacing={2}>
                <TextField
                  {...register('firstName')}
                  size="medium"
                  label="First Name"
                  disabled={!hasPermission}
                  sx={{ flex: 1 }}
                  data-vy="settings-members-invite-first-name"
                />
                <TextField
                  {...register('lastName')}
                  size="medium"
                  label="Last Name"
                  disabled={!hasPermission}
                  sx={{ flex: 1 }}
                  data-vy="settings-members-invite-last-name"
                />
                <Controller
                  control={control}
                  name="role"
                  render={({ field }) => (
                    <Autocomplete
                      autoComplete
                      disableClearable
                      disabled={!hasPermission}
                      size="medium"
                      options={roleOptions}
                      getOptionLabel={(option) => option.label}
                      value={mapRoleToLabelValuePair(field.value)}
                      onChange={(_, newValue) => {
                        if (newValue) field.onChange(newValue.role);
                      }}
                      isOptionEqualToValue={(option, value) => option.role === value.role}
                      renderInput={(params) => <TextField {...params} label="Role" size="medium" />}
                      sx={{ minWidth: 150 }}
                    />
                  )}
                />
              </Stack>

              <Stack direction="row" spacing={2} sx={{
                alignItems: "center"
              }}>
                <TextField
                  {...register('email')}
                  size="medium"
                  label="Email"
                  placeholder="example@mail.com"
                  disabled={!hasPermission}
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<VyIcon icon="AddUser" />}
                  disabled={!isValid}
                  onClick={onSubmit}
                  sx={{ alignSelf: 'flex-end', padding: '10px 20px' }}
                >
                  Invite
                </Button>
              </Stack>
            </Stack>
          ))}
        </Box>
      </Stack>
      {renderWithPermissions(
        [PERMISSIONS.USER_INVITE],
        (hasPermission) => hasPermission && <InvitationsTable />,
      )}
    </Stack>
  );
};

function mapRoleToLabelValuePair(role: Role): RoleLabelValuePair {
  return { label: capitalize(role), role };
}
